import { useEffect, useId, useRef, useState } from "react";
import { attachmentContentType, safeAttachmentName, validateAttachments, ATTACHMENT_ACCEPT } from "@/lib/attachment-upload";
import { Paperclip, Send, AlertCircle, X } from "lucide-react";
import shopPhotoAsset from "@/assets/shop-photo.jpg.asset.json";
import { z } from "zod";
import { track } from "@/lib/analytics";
import { checkSubmission, honeypotInputProps } from "@/lib/spam-guard";
import { supabase } from "@/integrations/supabase/client";
import { submitQuoteRequest } from "@/lib/submit-quote-request";
import {
  CONTACT_METHODS,
  ContactMethodPicker,
  type ContactMethod,
  type ContactMethodValue,
} from "@/components/form-fields/ContactMethodPicker";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB per file
const MAX_FILES = 8;

const PROJECT_TYPES = [
  "Custom Awning",
  "Patio / Pool Cover",
  "Sail Shade / Tension Structure",
  "Indoor / Outdoor Curtains",
  "Ag Tarp / Stock Trailer",
  "Boat Cover / Marine Canvas",
  "Pool & Spa Cover",
  "School / Athletic Field Cover",
  "Aluminum Welding / Fabrication",
  "Outdoor Building Lights",
  "Outdoor Tension Structure Lighting",
  "Outdoor Comfort Lighting",
  "Outdoor HVAC",
  "Outdoor Heating",
  "Outdoor Misting Fan",
  "Other",
];

const formSchema = z.object({
  firstName: z.string().trim().min(1, "Please enter your first name").max(80),
  lastName: z.string().trim().min(1, "Please enter your last name").max(80),
  phone: z
    .string()
    .trim()
    .min(7, "Please enter a valid phone number")
    .max(25, "Phone number is too long")
    .regex(/^[0-9+()\-.\s]+$/, "Phone can only contain numbers and ()-+. characters"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email is too long"),
  address: z.string().trim().min(5, "Please enter your address").max(300, "Address is too long"),
  projectType: z.string().refine((v) => PROJECT_TYPES.includes(v), "Please choose a project type"),
  contactMethod: z.enum(CONTACT_METHODS, { message: "Choose a preferred contact method" }),
  notes: z.string().trim().min(10, "Add a few details so we can quote accurately").max(2000, "Notes are too long"),
});

type FormErrors = Partial<Record<keyof z.infer<typeof formSchema>, string>>;

type ExtraFieldSpec = { name: string; label: string; multiple?: boolean };

type LetsTalkFormProps = {
  imageSrc?: string;
  imageAlt?: string;
  imageClassName?: string;
  eyebrow?: React.ReactNode;
  heading?: React.ReactNode;
  intro?: React.ReactNode;
  /** Optional extra form fields rendered above the notes textarea. */
  extraFields?: React.ReactNode;
  /** Named form inputs whose values should be prepended to the submitted notes as "Label: value". */
  extraFieldsSummary?: ReadonlyArray<ExtraFieldSpec>;
};

export function LetsTalkForm({ imageSrc, imageAlt, imageClassName, eyebrow, heading, intro, extraFields, extraFieldsSummary }: LetsTalkFormProps = {}) {
  const fid = useId();
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [projectType, setProjectType] = useState("");
  const [notes, setNotes] = useState("");
  const [contactMethod, setContactMethod] = useState<ContactMethodValue>("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const startedAt = useRef<number>(Date.now());
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Prefill data travels in the URL fragment (`/#lets-talk?type=...&notes=...`)
    // so internal CTA links stay clean, crawlable URLs instead of generating an
    // endless set of query-parameter duplicates. Legacy `?type=` links still work.
    const rawHash = window.location.hash.replace(/^#/, "");
    const [hashAnchor, hashQuery] = rawHash.split("?");
    const params = new URLSearchParams(hashQuery || window.location.search);
    const type = params.get("type");
    const note = params.get("notes");
    let didSet = false;
    if (type && PROJECT_TYPES.includes(type)) {
      setProjectType(type);
      didSet = true;
    }
    if (note) {
      setNotes(note);
      didSet = true;
    }
    if (didSet && hashAnchor === "lets-talk") {
      requestAnimationFrame(() => {
        document.getElementById("lets-talk")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, []);

  const clearError = (field: keyof FormErrors) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const raw = {
      firstName: String(fd.get("firstName") || ""),
      lastName: String(fd.get("lastName") || ""),
      phone: String(fd.get("phone") || ""),
      email: String(fd.get("email") || ""),
      address: String(fd.get("address") || ""),
      projectType: String(fd.get("projectType") || ""),
      contactMethod: String(fd.get("contactMethod") || "") as ContactMethod,
      notes: String(fd.get("notes") || ""),
    };
    const parsed = formSchema.safeParse(raw);
    if (!parsed.success) {
      const next: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormErrors;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      track("quote_form_submit", {
        target: "lets_talk",
        metadata: { status: "validation_error", fields: Object.keys(next).join(",") },
      });
      // Focus first invalid field
      const firstKey = Object.keys(next)[0];
      if (firstKey) {
        document.querySelector<HTMLElement>(`[name="${firstKey}"]`)?.focus();
      }
      return;
    }
    setErrors({});
    const data = parsed.data;

    const guard = await checkSubmission({
      formId: "lets_talk",
      formData: fd,
      startedAt: startedAt.current,
      payload: { email: data.email, projectType: data.projectType, notes: data.notes },
    });
    if (!guard.ok) {
      if (guard.reason === "honeypot") return;
      setFormError(guard.message ?? "Please try again.");
      track("quote_form_submit", { target: "lets_talk", metadata: { status: "blocked", reason: guard.reason } });
      return;
    }
    setFormError(null);

    // Upload attachments to private storage. We persist the storage_path to the
    // database (custom_quote_attachments) so even if the email link fails, the
    // admin dashboard can always retrieve the files.
    type Uploaded = { path: string; name: string; type: string; size: number };
    let uploaded: Uploaded[] = [];
    const attachErr = validateAttachments(files);
    if (attachErr) {
      setFormError(attachErr);
      return;
    }
    if (files.length > 0) {
      setUploading(true);
      try {
        const stamp = Date.now();
        const folder = `lets-talk/${crypto.randomUUID()}`;
        uploaded = await Promise.all(
          files.map(async (file, i) => {
            const safeName = safeAttachmentName(file);
            const path = `${folder}/${i}-${safeName}`;
            const { error } = await supabase.storage
              .from("contact-uploads")
              .upload(path, file, { contentType: attachmentContentType(file) ?? "image/jpeg", upsert: false });
            if (error) throw error;
            return {
              path,
              name: file.name,
              type: attachmentContentType(file) ?? "image/jpeg",
              size: file.size,
            };
          })
        );
      } catch (err) {
        setUploading(false);
        setFormError("We couldn't upload one of your photos. Please try again or email them directly.");
        return;
      }
      setUploading(false);
    }

    track("quote_form_submit", {
      target: "lets_talk",
      metadata: {
        project_type: data.projectType,
        contact_method: data.contactMethod,
        attachments: files.length,
      },
    });
    const fullName = `${data.firstName} ${data.lastName}`.trim();
    const attachmentsNote =
      uploaded.length > 0
        ? `\n\n(${uploaded.length} photo${uploaded.length === 1 ? "" : "s"}/file${uploaded.length === 1 ? "" : "s"} attached — view in the admin dashboard or the notification email.)`
        : "";
    const extraLines: string[] = [];
    if (extraFieldsSummary?.length) {
      for (const spec of extraFieldsSummary) {
        const values = spec.multiple ? fd.getAll(spec.name).map(String) : [String(fd.get(spec.name) ?? "")];
        const cleaned = values.map((v) => v.trim()).filter(Boolean);
        if (cleaned.length) extraLines.push(`${spec.label}: ${cleaned.join(", ")}`);
      }
    }
    const fullNotes =
      `Project Type: ${data.projectType}\n` +
      `Preferred Contact: ${data.contactMethod}\n` +
      (extraLines.length ? extraLines.join("\n") + "\n" : "") +
      `\n${data.notes}${attachmentsNote}`;

    const { error: dbError, id: quoteId } = await submitQuoteRequest({
      formId: "lets_talk_form",
      productId: "lets-talk",
      productName: data.projectType,
      customerName: fullName,
      customerEmail: data.email,
      customerPhone: data.phone,
      shippingAddress: data.address,
      notes: fullNotes,
    });
    if (dbError) {
      setFormError("We couldn't submit your request. Please call +1 (830) 390-9588 or try again.");
      track("quote_form_submit", { target: "lets_talk", metadata: { status: "db_error", message: dbError.message } });
      return;
    }

    // Persist attachments to DB so they're never lost, even if the email fails.
    if (quoteId && uploaded.length > 0) {
      const { error: attachErr } = await supabase.from("custom_quote_attachments").insert(
        uploaded.map((u, i) => ({
          quote_request_id: quoteId,
          file_url: u.path, // storage path; signed URLs are generated server-side for email
          storage_path: u.path,
          file_name: u.name,
          content_type: u.type,
          size_bytes: u.size,
          sort_order: i,
        })),
      );
      if (attachErr) {
        // Non-fatal: the lead is already saved. Log for diagnostics.
        console.error("[lets-talk] attachment row insert failed", attachErr);
      }
    }

    track("quote_form_submit", {
      target: "lets_talk",
      metadata: { status: "success", project_type: data.projectType },
    });
    setSubmitted(true);
  };


  const inputClass = (field: keyof FormErrors) =>
    `w-full border bg-white px-4 py-3 text-sm focus:outline-none ${
      errors[field]
        ? "border-red focus:border-red"
        : "border-input focus:border-navy-deep"
    }`;

  const FieldError = ({ field }: { field: keyof FormErrors }) =>
    errors[field] ? (
      <p
        id={`${field}-error`}
        role="alert"
        className="mt-1 flex items-start gap-1 text-xs font-medium text-red"
      >
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>{errors[field]}</span>
      </p>
    ) : null;

  return (
    <section
      id="lets-talk"
      className="scroll-mt-24 border-t-4 border-navy-deep bg-cream py-20"
    >
      <div className="mx-auto max-w-5xl px-4 lg:px-8">
        <div className="grid gap-10 md:grid-cols-5">
          <div className="md:col-span-2">
            <div className="font-display text-sm tracking-[0.3em] text-navy-deep">{eyebrow ?? "★ LET'S TALK"}</div>
            <h3 className="mt-2 font-display text-3xl font-bold text-navy-deep sm:text-4xl lg:text-5xl">{heading ?? "Tell Us About Your Project"}</h3>
            <p className="mt-4 text-lg text-muted-foreground">
              {intro ?? "Send us the basics, drop a few photos, and we'll get back to you with a free quote — usually within one business day."}
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Prefer to call? <a href="tel:+18303909588" className="font-semibold text-navy-deep underline">+1 (830) 390-9588</a><br />
              Email: <a href="mailto:office@texasmadeshade.com" className="font-semibold text-navy-deep underline">office@texasmadeshade.com</a>
            </p>
            <div className={imageClassName ?? "mt-6 overflow-hidden border-4 border-navy-deep shadow-card"}>
              <img
                src={imageSrc ?? shopPhotoAsset.url}
                alt={imageAlt ?? "Texas Made Shade fabrication shop in Seguin, TX"}
                className="h-auto w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <form onSubmit={handleSubmit} noValidate className="md:col-span-3 space-y-4 border-l-4 border-navy-deep bg-white p-6 shadow-card md:p-8">
            <input {...honeypotInputProps()} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor={`${fid}-firstName`} className="block font-display text-xs font-bold uppercase tracking-wider text-navy-deep">
                  First Name <span className="text-red">*</span>
                </label>
                <input
                  id={`${fid}-firstName`}
                  name="firstName"
                  autoComplete="given-name"
                  required
                  placeholder="First Name"
                  className={`mt-1 ${inputClass("firstName")}`}
                  aria-invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                  onChange={() => clearError("firstName")}
                  maxLength={80}
                />
                <FieldError field="firstName" />
              </div>
              <div>
                <label htmlFor={`${fid}-lastName`} className="block font-display text-xs font-bold uppercase tracking-wider text-navy-deep">
                  Last Name <span className="text-red">*</span>
                </label>
                <input
                  id={`${fid}-lastName`}
                  name="lastName"
                  autoComplete="family-name"
                  required
                  placeholder="Last Name"
                  className={`mt-1 ${inputClass("lastName")}`}
                  aria-invalid={!!errors.lastName}
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                  onChange={() => clearError("lastName")}
                  maxLength={80}
                />
                <FieldError field="lastName" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor={`${fid}-email`} className="block font-display text-xs font-bold uppercase tracking-wider text-navy-deep">
                  Email Address <span className="text-red">*</span>
                </label>
                <input
                  id={`${fid}-email`}
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  className={`mt-1 ${inputClass("email")}`}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  onChange={() => clearError("email")}
                  maxLength={255}
                />
                <FieldError field="email" />
              </div>
              <div>
                <label htmlFor={`${fid}-phone`} className="block font-display text-xs font-bold uppercase tracking-wider text-navy-deep">
                  Phone Number <span className="text-red">*</span>
                </label>
                <input
                  id={`${fid}-phone`}
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  placeholder="(830) 555-0123"
                  className={`mt-1 ${inputClass("phone")}`}
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                  onChange={() => clearError("phone")}
                  maxLength={25}
                />
                <FieldError field="phone" />
              </div>
            </div>
            <div>
              <label htmlFor={`${fid}-address`} className="block font-display text-xs font-bold uppercase tracking-wider text-navy-deep">
                Project Address <span className="text-red">*</span>
              </label>
              <input
                id={`${fid}-address`}
                name="address"
                autoComplete="address-line1"
                required
                placeholder="Street, city, state, ZIP"
                className={`mt-1 ${inputClass("address")}`}
                aria-invalid={!!errors.address}
                aria-describedby={errors.address ? "address-error" : undefined}
                onChange={() => clearError("address")}
                maxLength={300}
              />
              <FieldError field="address" />
            </div>
            <div>
              <label htmlFor={`${fid}-projectType`} className="block font-display text-xs font-bold uppercase tracking-wider text-navy-deep">
                Project Type <span className="text-red">*</span>
              </label>
              <select
                id={`${fid}-projectType`}
                name="projectType"
                required
                value={projectType}
                onChange={(e) => {
                  setProjectType(e.target.value);
                  clearError("projectType");
                }}
                className={`mt-1 ${inputClass("projectType")}`}
                aria-invalid={!!errors.projectType}
                aria-describedby={errors.projectType ? "projectType-error" : undefined}
              >
                <option value="" disabled>Project Type…</option>
                {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <FieldError field="projectType" />
            </div>
            <ContactMethodPicker
              value={contactMethod}
              onChange={(m) => {
                setContactMethod(m);
                clearError("contactMethod");
              }}
              error={errors.contactMethod}
            />
            {extraFields}
            <div>
              <label htmlFor={`${fid}-notes`} className="block font-display text-xs font-bold uppercase tracking-wider text-navy-deep">
                Project Notes
              </label>
              <textarea
                id={`${fid}-notes`}
                name="notes"
                rows={5}
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  clearError("notes");
                }}
                placeholder="Project notes — size, fabric color, timeline, anything helpful…"
                className={`mt-1 ${inputClass("notes")}`}
                aria-invalid={!!errors.notes}
                aria-describedby={errors.notes ? "notes-error" : undefined}
                maxLength={2000}
              />
              <FieldError field="notes" />
            </div>
            <div>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const incoming = Array.from(e.target.files ?? []);
                  const merged = [...files];
                  let err: string | null = null;
                  for (const f of incoming) {
                    if (f.size > MAX_FILE_SIZE) {
                      err = `"${f.name}" is over 10 MB. Please attach a smaller file.`;
                      continue;
                    }
                    if (merged.length >= MAX_FILES) {
                      err = `You can attach up to ${MAX_FILES} files.`;
                      break;
                    }
                    if (!merged.some((m) => m.name === f.name && m.size === f.size)) {
                      merged.push(f);
                    }
                  }
                  setFiles(merged);
                  setFileError(err);
                  // Reset so selecting the same file again still fires onChange
                  if (fileInput.current) fileInput.current.value = "";
                }}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className="inline-flex items-center gap-2 border-2 border-navy-deep bg-white px-5 py-3 font-display text-xs uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                >
                  <Paperclip className="h-4 w-4" /> Attach Photos / Files <span className="font-normal normal-case tracking-normal text-navy-deep/60">(optional)</span>
                  {files.length > 0 && <span className="ml-1 rounded-full bg-navy-deep px-2 py-0.5 text-xs text-white">{files.length}</span>}
                </button>
                <button
                  type="submit"
                  disabled={!contactMethod || uploading}
                  className="inline-flex items-center gap-2 bg-navy-deep px-7 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-navy disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="h-4 w-4" /> {uploading ? "Uploading…" : "Send Request"}
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Add up to {MAX_FILES} reference photos or PDFs (max 10 MB each) — site pictures, sketches, inspiration, anything that helps us quote your project.
              </p>
              {fileError && (
                <p role="alert" className="mt-1 text-xs font-medium text-red">{fileError}</p>
              )}
              {files.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                  {files.map((f, idx) => (
                    <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2 border border-navy-deep/10 bg-cream/50 px-2 py-1">
                      <span className="truncate">📎 {f.name} <span className="text-navy-deep/70">({(f.size / 1024 / 1024).toFixed(2)} MB)</span></span>
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                        aria-label={`Remove ${f.name}`}
                        className="shrink-0 text-navy-deep/60 transition hover:text-red"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {formError && (
              <p role="alert" className="text-sm font-medium text-red">{formError}</p>
            )}
            {submitted && Object.keys(errors).length === 0 && (
              <div
                role="status"
                aria-live="polite"
                className="border-l-4 border-navy-deep bg-cream p-4 text-sm text-navy-deep shadow-card"
              >
                <div className="font-display text-xs uppercase tracking-wider">
                  ✓ Quote requested
                </div>
                <p className="mt-2">
                  Thanks — we received your request and any uploaded photos. We'll be in touch shortly. Prefer to call? <a href="tel:+18303909588" className="font-semibold underline">+1 (830) 390-9588</a>.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
